import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useDarkMode } from '../../App';

export default function Packages() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [portfolioOpen, setPortfolioOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const { isDarkMode, toggleDarkMode } = useDarkMode();
  const [heroVisible, setHeroVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setHeroVisible(true), 300);
    return () => clearTimeout(timer);
  }, []);

  // Scroll animation observer
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px',
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-visible');
        }
      });
    }, observerOptions);

    const elements = document.querySelectorAll(
      '.scroll-reveal, .scroll-reveal-left, .scroll-reveal-right, .scroll-reveal-scale'
    );
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const faqs = [
    {
      question: 'What kind of stories does Cin\u00e9story produce?',
      answer:
        'We create short films, brand documentaries, personal brand narratives, and thoughtful visual content. Everything is tailored to reveal the human side of your brand \u2014 not just sell a product.',
    },
    {
      question: 'How long does the brand analysis take?',
      answer:
        'The complete brand analysis and strategy development typically takes 7-10 business days from the time you complete our discovery form. We take the time to thoroughly analyze your social media presence and develop a comprehensive strategy tailored to your specific needs.',
    },
    {
      question: 'Do you create the content for me?',
      answer:
        'We provide you with a complete content strategy, including 15-20 personalized content ideas, hooks, captions, and posting schedules. You create the content based on our strategic direction, or you can upgrade to our Premium Done-With-You package for ongoing content direction and support.',
    },
    {
      question: 'What platforms do you analyze?',
      answer:
        'We analyze Instagram, TikTok, and YouTube to give you a comprehensive view of your social media performance. Our analysis includes engagement patterns, content performance, visual identity, and competitor comparisons across all three platforms.',
    },
    {
      question: 'Can I upgrade my package later?',
      answer:
        "Absolutely! Many of our clients start with the Brand Blueprint Package and upgrade to the Full Brand Engine or Premium Done-With-You as their business grows. We make the transition seamless and build upon the foundation we've already created.",
    },
    {
      question: 'Is Cin\u00e9story only for real estate professionals?',
      answer:
        'Not at all. While we have deep roots in real estate through Cin\u00e9state, Cin\u00e9story works with coaches, consultants, dentists, lawyers, creators, and any service-based business looking to build authority and attract clients through authentic storytelling.',
    },
    {
      question: "What if I'm not satisfied with the strategy?",
      answer:
        "We work closely with you throughout the process and include revision rounds to ensure the strategy aligns with your vision. Our goal is to deliver a strategy you're excited to implement. If you're not satisfied, we'll work with you until you are.",
    },
  ];

  return (
    <div
      className={`min-h-screen ${
        isDarkMode ? 'bg-[#1a1a1a] text-white' : 'bg-white text-[#000000]'
      } transition-colors duration-500`}
      style={{ fontFamily: "'Inter', sans-serif" }}
    >
      {/* CSS for scroll animations */}
      <style>{`
        .scroll-reveal {
          opacity: 0;
          transform: translateY(40px);
          transition: opacity 0.8s cubic-bezier(0.16, 1, 0.3, 1), transform 0.8s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .scroll-reveal.animate-visible {
          opacity: 1;
          transform: translateY(0);
        }
        .scroll-reveal-delay-1 { transition-delay: 0.1s; }
        .scroll-reveal-delay-2 { transition-delay: 0.2s; }
        .scroll-reveal-delay-3 { transition-delay: 0.3s; }
        .scroll-reveal-delay-4 { transition-delay: 0.4s; }
        .scroll-reveal-scale {
          opacity: 0;
          transform: scale(0.95);
          transition: opacity 0.8s cubic-bezier(0.16, 1, 0.3, 1), transform 0.8s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .scroll-reveal-scale.animate-visible {
          opacity: 1;
          transform: scale(1);
        }
        .scroll-reveal-left {
          opacity: 0;
          transform: translateX(-40px);
          transition: opacity 0.8s cubic-bezier(0.16, 1, 0.3, 1), transform 0.8s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .scroll-reveal-left.animate-visible {
          opacity: 1;
          transform: translateX(0);
        }
        .scroll-reveal-right {
          opacity: 0;
          transform: translateX(40px);
          transition: opacity 0.8s cubic-bezier(0.16, 1, 0.3, 1), transform 0.8s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .scroll-reveal-right.animate-visible {
          opacity: 1;
          transform: translateX(0);
        }
        @keyframes hero-reveal {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes hero-line-expand {
          from { width: 0; }
          to { width: 120px; }
        }
        @keyframes hero-fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .hero-title {
          opacity: 0;
          animation: hero-reveal 1.2s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
        .hero-subtitle {
          opacity: 0;
          animation: hero-reveal 1.2s cubic-bezier(0.16, 1, 0.3, 1) 0.4s forwards;
        }
        .hero-line {
          width: 0;
          animation: hero-line-expand 1s cubic-bezier(0.16, 1, 0.3, 1) 0.8s forwards;
        }
        .hero-cta {
          opacity: 0;
          animation: hero-reveal 1.2s cubic-bezier(0.16, 1, 0.3, 1) 1s forwards;
        }
        .hero-scroll {
          opacity: 0;
          animation: hero-fade-in 1s ease 1.6s forwards;
        }
      `}</style>

      {/* Top Right Icons */}
      <div className="fixed top-4 right-4 md:top-8 md:right-8 flex items-center gap-3 md:gap-4 z-50">
        <button
          onClick={toggleDarkMode}
          className={`w-9 h-9 md:w-10 md:h-10 flex items-center justify-center ${
            isDarkMode
              ? 'text-white/70 hover:text-white'
              : 'text-[#000000]/70 hover:text-[#000000]'
          } transition-colors cursor-pointer`}
        >
          <i className={`ri-${isDarkMode ? 'moon' : 'sun'}-line text-lg md:text-xl`}></i>
        </button>

        <button
          onClick={() => setMenuOpen(!menuOpen)}
          className={`w-9 h-9 md:w-10 md:h-10 flex items-center justify-center ${
            isDarkMode
              ? 'text-white/70 hover:text-white'
              : 'text-[#000000]/70 hover:text-[#000000]'
          } transition-colors cursor-pointer`}
        >
          <i className="ri-menu-line text-lg md:text-xl"></i>
        </button>
      </div>

      {/* Slide-out Menu */}
      <div
        className={`fixed top-0 right-0 h-full w-72 md:w-80 ${
          isDarkMode ? 'bg-[#0d0d0d]' : 'bg-[#F2F2F2]'
        } shadow-2xl transform transition-transform duration-500 z-40 ${
          menuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="p-6 md:p-8">
          <button
            onClick={() => setMenuOpen(false)}
            className={`absolute top-6 right-6 md:top-8 md:right-8 w-9 h-9 md:w-10 md:h-10 flex items-center justify-center ${
              isDarkMode
                ? 'text-white/70 hover:text-white'
                : 'text-[#000000]/70 hover:text-[#000000]'
            } transition-colors cursor-pointer`}
          >
            <i className="ri-close-line text-lg md:text-xl"></i>
          </button>

          <nav className="mt-16 md:mt-20 space-y-5 md:space-y-6">
            <Link
              to="/home"
              onClick={() => setMenuOpen(false)}
              className={`block text-sm md:text-base font-normal tracking-[0.15em] uppercase ${
                isDarkMode
                  ? 'text-white/90 hover:text-white'
                  : 'text-[#000000]/90 hover:text-[#000000]'
              } transition-colors`}
            >
              Home
            </Link>
            <a
              href="https://order.cinestate.ca/booking/new"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setMenuOpen(false)}
              className={`block text-sm md:text-base font-normal tracking-[0.15em] uppercase ${
                isDarkMode
                  ? 'text-white/90 hover:text-white'
                  : 'text-[#000000]/90 hover:text-[#000000]'
              } transition-colors`}
            >
              Listing Packages
            </a>
            <Link
              to="/packages"
              onClick={() => setMenuOpen(false)}
              className={`block text-sm md:text-base font-normal tracking-[0.15em] uppercase ${
                isDarkMode
                  ? 'text-white/90 hover:text-white'
                  : 'text-[#000000]/90 hover:text-[#000000]'
              } transition-colors`}
            >
              Content Packages
            </Link>
            <Link
              to="/staging"
              onClick={() => setMenuOpen(false)}
              className={`block text-sm md:text-base font-normal tracking-[0.15em] uppercase ${
                isDarkMode
                  ? 'text-white/90 hover:text-white'
                  : 'text-[#000000]/90 hover:text-[#000000]'
              } transition-colors`}
            >
              Home Staging
            </Link>
            {/* Portfolio Dropdown */}
            <div>
              <button
                onClick={() => setPortfolioOpen(!portfolioOpen)}
                className={`flex items-center justify-between w-full text-sm md:text-base font-normal tracking-[0.15em] uppercase ${
                  isDarkMode ? 'text-white/90 hover:text-white' : 'text-[#000000]/90 hover:text-[#000000]'
                } transition-colors cursor-pointer`}
              >
                <span>Portfolio</span>
                <i className={`${portfolioOpen ? 'ri-subtract-line' : 'ri-add-line'} text-xs`}></i>
              </button>
              <div className={`overflow-hidden transition-all duration-300 ${portfolioOpen ? 'max-h-24 mt-3' : 'max-h-0'}`}>
                <div className="ml-4 space-y-3">
                  <Link
                    to="/portfolio"
                    onClick={() => setMenuOpen(false)}
                    className={`block text-xs md:text-sm font-normal tracking-[0.15em] uppercase ${isDarkMode ? 'text-white/55 hover:text-white' : 'text-[#000000]/55 hover:text-[#000000]'} transition-colors`}
                  >
                    Residential
                  </Link>
                  <Link
                    to="/portfolio/commercial"
                    onClick={() => setMenuOpen(false)}
                    className={`block text-xs md:text-sm font-normal tracking-[0.15em] uppercase ${isDarkMode ? 'text-white/55 hover:text-white' : 'text-[#000000]/55 hover:text-[#000000]'} transition-colors`}
                  >
                    Commercial
                  </Link>
                </div>
              </div>
            </div>
            <a
              href="/home#contact"
              onClick={() => setMenuOpen(false)}
              className={`block text-sm md:text-base font-normal tracking-[0.15em] uppercase ${
                isDarkMode
                  ? 'text-white/90 hover:text-white'
                  : 'text-[#000000]/90 hover:text-[#000000]'
              } transition-colors`}
            >
              Contact
            </a>
          </nav>

          <div className="absolute bottom-8 md:bottom-12 left-6 md:left-8 flex gap-3 md:gap-4">
            <a
              href="https://www.instagram.com/kianikanstudio?igsh=M3RoZ25wc25oZHY0"
              target="_blank"
              rel="noopener noreferrer"
              className={`w-9 h-9 md:w-10 md:h-10 flex items-center justify-center border ${
                isDarkMode
                  ? 'border-white/20 text-white/70 hover:border-white hover:text-white'
                  : 'border-[#000000]/20 text-[#000000]/70 hover:border-[#000000] hover:text-[#000000]'
              } transition-colors`}
            >
              <i className="ri-instagram-line text-sm md:text-base"></i>
            </a>
            <a
              href="https://youtube.com/@kianikanstudio3017?si=QvudTCaWtHR5bmy8"
              target="_blank"
              rel="noopener noreferrer"
              className={`w-9 h-9 md:w-10 md:h-10 flex items-center justify-center border ${
                isDarkMode
                  ? 'border-white/20 text-white/70 hover:border-white hover:text-white'
                  : 'border-[#000000]/20 text-[#000000]/70 hover:border-[#000000] hover:text-[#000000]'
              } transition-colors`}
            >
              <i className="ri-youtube-line text-sm md:text-base"></i>
            </a>
            <a
              href="https://www.tiktok.com/@kianikanstudio.ca?_r=1&_t=ZS-91yzSCjzfEY"
              target="_blank"
              rel="noopener noreferrer"
              className={`w-9 h-9 md:w-10 md:h-10 flex items-center justify-center border ${
                isDarkMode
                  ? 'border-white/20 text-white/70 hover:border-white hover:text-white'
                  : 'border-[#000000]/20 text-[#000000]/70 hover:border-[#000000] hover:text-[#000000]'
              } transition-colors`}
            >
              <i className="ri-tiktok-line text-sm md:text-base"></i>
            </a>
            <a
              href="https://www.instagram.com/kianikanstudio?igsh=M3RoZ25wc25oZHY0"
              target="_blank"
              rel="noopener noreferrer"
              className={`w-9 h-9 md:w-10 md:h-10 flex items-center justify-center border ${
                isDarkMode
                  ? 'border-white/20 text-white/70 hover:border-white hover:text-white'
                  : 'border-[#000000]/20 text-[#000000]/70 hover:border-[#000000] hover:text-[#000000]'
              } transition-colors`}
            >
              <i className="ri-facebook-fill text-sm md:text-base"></i>
            </a>
          </div>
        </div>
      </div>

      {/* Overlay */}
      {menuOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30"
          onClick={() => setMenuOpen(false)}
        ></div>
      )}

      {/* Logo - Top Left: CIN\u00c9STORY logo */}
      <div className="fixed top-4 left-4 md:top-8 md:left-8 z-40">
        <Link to="/" className="block">
          <img
            src="https://static.readdy.ai/image/530c8f921e7d182176cffe22debeaa7c/f67bfe48594cc7cd8567f21111a8980c.png"
            alt="CIN\u00c9STORY"
            className={`h-5 md:h-7 w-auto ${isDarkMode ? 'brightness-0 invert' : ''}`}
          />
        </Link>
      </div>

      {/* ────────────────────────── HERO SECTION — Bold Cinematic ────────────────────────── */}
      <section className={`relative h-screen w-full overflow-hidden ${isDarkMode ? 'bg-[#0a0a0a]' : 'bg-white'}`}>
        {/* Hero Content */}
        <div className="relative z-10 h-full flex flex-col items-center justify-center px-6 md:px-8 w-full">
          <div className="max-w-5xl mx-auto text-center w-full">
            {heroVisible && (
              <>
                <p
                  className={`hero-subtitle text-[10px] sm:text-xs md:text-sm tracking-[0.4em] uppercase ${isDarkMode ? 'text-white/40' : 'text-[#000000]/40'} mb-6 md:mb-8`}
                >
                  A CIN&Eacute;STATE BRAND
                </p>

                <div className="hero-title flex justify-center">
                  <img
                    src="https://static.readdy.ai/image/530c8f921e7d182176cffe22debeaa7c/f67bfe48594cc7cd8567f21111a8980c.png"
                    alt="CINÉSTORY™"
                    className={`h-10 sm:h-14 md:h-20 lg:h-24 xl:h-28 w-auto ${isDarkMode ? 'brightness-0 invert' : ''}`}
                  />
                </div>

                <div className={`hero-line h-px ${isDarkMode ? 'bg-white/20' : 'bg-[#000000]/20'} mx-auto mt-8 md:mt-12 mb-8 md:mb-12`}></div>

                <p
                  className={`hero-subtitle text-xs sm:text-sm md:text-base lg:text-lg xl:text-xl font-light ${isDarkMode ? 'text-white/60' : 'text-[#000000]/60'} max-w-4xl mx-auto leading-relaxed tracking-wide px-4`}
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  Where stories don&apos;t just support the brand &mdash; they become the brand.
                </p>
              </>
            )}
          </div>

          {/* Scroll indicator */}
          <div className="hero-scroll absolute bottom-8 md:bottom-12 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3">
            <span className={`text-[9px] md:text-[10px] tracking-[0.3em] uppercase ${isDarkMode ? 'text-white/30' : 'text-[#000000]/30'}`}>SCROLL</span>
            <div className={`w-px h-8 md:h-12 ${isDarkMode ? 'bg-gradient-to-b from-white/30 to-transparent' : 'bg-gradient-to-b from-black/30 to-transparent'}`}></div>
          </div>
        </div>
      </section>

      {/* ────────────────────────── BRAND PHILOSOPHY SECTION ────────────────────────── */}
      <section
        className={`py-14 sm:py-20 md:py-32 px-4 sm:px-6 md:px-8 lg:px-16 ${
          isDarkMode ? 'bg-[#0d0d0d]' : 'bg-[#F2F2F2]'
        }`}
      >
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-16 lg:gap-24 items-center">
            <div className="scroll-reveal">
              <p
                className={`text-[10px] md:text-xs tracking-[0.3em] uppercase mb-4 md:mb-6 ${
                  isDarkMode ? 'text-white/40' : 'text-[#000000]/40'
                }`}
              >
                THE PHILOSOPHY
              </p>
              <h2
                className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-normal leading-[1.2] tracking-wide mb-6 md:mb-8"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                CINÉSTORY™ is where personal brands take shape. Where experiences turn into narratives.
              </h2>
              <div className={`w-12 h-px ${isDarkMode ? 'bg-white/20' : 'bg-[#000000]/20'} mb-6 md:mb-8`}></div>
              <p
                className={`text-sm md:text-lg font-light leading-relaxed ${
                  isDarkMode ? 'text-white/70' : 'text-[#000000]/70'
                }`}
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                Short films. Thoughtful visuals. Stories that feel human &mdash; not scripted. Because the strongest brands
                aren’t built on algorithms. They’re built on trust.
              </p>
            </div>
            <div className="scroll-reveal scroll-reveal-delay-2">
              <div className="grid grid-cols-2 gap-3 md:gap-4">
                <div
                  className={`p-5 md:p-8 rounded-lg ${
                    isDarkMode ? 'bg-white/[0.03] border border-white/5' : 'bg-white border border-[#000000]/5'
                  }`}
                >
                  <div className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center mb-3 md:mb-4">
                    <i
                      className={`ri-film-line text-xl md:text-2xl ${
                        isDarkMode ? 'text-white/60' : 'text-[#000000]/60'
                      }`}
                    ></i>
                  </div>
                  <p className="text-xs md:text-sm font-normal tracking-wide uppercase">Short Films</p>
                </div>
                <div
                  className={`p-5 md:p-8 rounded-lg ${
                    isDarkMode ? 'bg-white/[0.03] border border-white/5' : 'bg-white border border-[#000000]/5'
                  }`}
                >
                  <div className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center mb-3 md:mb-4">
                    <i
                      className={`ri-eye-line text-xl md:text-2xl ${
                        isDarkMode ? 'text-white/60' : 'text-[#000000]/60'
                      }`}
                    ></i>
                  </div>
                  <p className="text-xs md:text-sm font-normal tracking-wide uppercase">Thoughtful Visuals</p>
                </div>
                <div
                  className={`p-5 md:p-8 rounded-lg ${
                    isDarkMode ? 'bg-white/[0.03] border border-white/5' : 'bg-white border border-[#000000]/5'
                  }`}
                >
                  <div className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center mb-3 md:mb-4">
                    <i
                      className={`ri-user-heart-line text-xl md:text-2xl ${
                        isDarkMode ? 'text-white/60' : 'text-[#000000]/60'
                      }`}
                    ></i>
                  </div>
                  <p className="text-xs md:text-sm font-normal tracking-wide uppercase">Human Stories</p>
                </div>
                <div
                  className={`p-5 md:p-8 rounded-lg ${
                    isDarkMode ? 'bg-white/[0.03] border border-white/5' : 'bg-white border border-[#000000]/5'
                  }`}
                >
                  <div className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center mb-3 md:mb-4">
                    <i
                      className={`ri-shield-check-line text-xl md:text-2xl ${
                        isDarkMode ? 'text-white/60' : 'text-[#000000]/60'
                      }`}
                    ></i>
                  </div>
                  <p className="text-xs md:text-sm font-normal tracking-wide uppercase">Built on Trust</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ────────────────────────── VIDEO SECTION ────────────────────────── */}
      <section
        id="video-section"
        className="py-12 sm:py-16 md:py-24 px-4 sm:px-6 md:px-8 lg:px-16"
      >
        <div className="max-w-4xl mx-auto">
          <p
            className="scroll-reveal text-[10px] md:text-xs tracking-[0.2em] sm:tracking-[0.3em] uppercase mb-3 sm:mb-4 md:mb-6 text-center"
          >
            SEE THE CIN&Eacute;STORY APPROACH
          </p>
          <div className="scroll-reveal scroll-reveal-delay-1 aspect-video w-full rounded-lg overflow-hidden shadow-2xl">
            <iframe
              src="https://www.youtube.com/embed/QS3c_FgOIQg"
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
          </div>
          <p
            className={`scroll-reveal scroll-reveal-delay-2 text-sm md:text-lg max-w-3xl mx-auto leading-relaxed mt-6 md:mt-10 text-center font-light ${
              isDarkMode ? 'text-white/70' : 'text-[#000000]/70'
            }`}
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            No guessing. No randomness. A proven storytelling system that grows your authority, refines your narrative, and attracts the right audience — consistently.
          </p>
          <div className="scroll-reveal scroll-reveal-delay-3 text-center mt-6 md:mt-10">
            <a
              href="/home#contact"
              className={`inline-block px-6 py-3 sm:px-8 sm:py-3 md:px-12 md:py-4 ${
                isDarkMode ? 'bg-white text-[#000000] hover:bg-white/90' : 'bg-[#000000] text-white hover:bg-[#000000]/90'
              } text-[10px] sm:text-xs md:text-sm tracking-widest uppercase transition-colors font-normal whitespace-nowrap cursor-pointer`}
            >
              START YOUR STORY
            </a>
          </div>
        </div>
      </section>

      {/* ────────────────────────── TESTIMONIALS ────────────────────────── */}
      <section
        id="testimonials"
        className={`py-14 sm:py-20 md:py-32 lg:py-40 px-4 sm:px-6 md:px-8 lg:px-16 ${
          isDarkMode ? 'bg-[#0d0d0d]' : 'bg-[#F2F2F2]'
        }`}
      >
        <div className="max-w-6xl mx-auto">
          <h2 className="scroll-reveal text-lg sm:text-xl md:text-3xl lg:text-4xl font-normal text-center mb-4 md:mb-8 tracking-wide uppercase">
            BRANDS &amp; CREATORS WE&apos;VE HELPED ELEVATE
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-12">
            {/* Testimonial 1 */}
            <div
              className={`scroll-reveal ${
                isDarkMode ? 'bg-[#1a1a1a]' : 'bg-white'
              } p-5 md:p-8 rounded-lg shadow-lg border ${
                isDarkMode ? 'border-white/5' : 'border-[#000000]/5'
              }`}
            >
              <div className="flex flex-col md:flex-row items-start gap-3 md:gap-6 mb-3 md:mb-6">
                <img
                  src="https://static.readdy.ai/image/530c8f921e7d182176cffe22debeaa7c/d6abaddb5b9d0f3690cccc290ca39893.jpeg"
                  alt="Client"
                  className="w-16 h-16 md:w-24 md:h-24 rounded-full object-cover mx-auto md:mx-0"
                />
                <div className="flex-1 text-center md:text-left">
                  <p
                    className={`text-xs md:text-lg mb-3 md:mb-6 leading-relaxed italic font-light ${
                      isDarkMode ? 'text-white/90' : 'text-[#000000]/90'
                    }`}
                    style={{ fontFamily: "'Cormorant Garamond', serif" }}
                  >
                    &quot;The strategic approach Cin\u00e9story brought to our brand was exactly what we needed. They didn't just give us content ideas— they helped us understand our audience on a deeper level and showed us how to position ourselves as industry leaders. The results speak for themselves: higher engagement, more qualified leads, and a brand presence that truly reflects our expertise.&quot;
                  </p>
                  <div>
                    <p className="font-normal text-sm md:text-lg uppercase">SHAWN TAHRIRIHA</p>
                    <p
                      className={`text-xs font-light ${
                        isDarkMode ? 'text-white/60' : 'text-[#000000]/60'
                      }`}
                      style={{ fontFamily: "'Cormorant Garamond', serif" }}
                    >
                      President &amp; CEO of Shaheen and Company | Broker of Record
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Testimonial 2 */}
            <div
              className={`scroll-reveal scroll-reveal-delay-1 ${
                isDarkMode ? 'bg-[#1a1a1a]' : 'bg-white'
              } p-5 md:p-8 rounded-lg shadow-lg border ${
                isDarkMode ? 'border-white/5' : 'border-[#000000]/5'
              }`}
            >
              <div className="flex flex-col md:flex-row items-start gap-3 md:gap-6 mb-3 md:mb-6">
                <img
                  src="https://static.readdy.ai/image/530c8f921e7d182176cffe22debeaa7c/a109f0765b6ab6dada4dc7ca9921264f.jpeg"
                  alt="Client"
                  className="w-16 h-16 md:w-24 md:h-24 rounded-full object-cover mx-auto md:mx-0"
                />
                <div className="flex-1 text-center md:text-left">
                  <p
                    className={`text-xs md:text-lg mb-3 md:mb-6 leading-relaxed italic font-light ${
                      isDarkMode ? 'text-white/90' : 'text-[#000000]/90'
                    }`}
                    style={{ fontFamily: "'Cormorant Garamond', serif" }}
                  >
                    &quot;Before working with this team, our social media felt scattered and inconsistent. Now we have a clear direction and a content system that actually drives business. What impressed me most was how they took the time to understand our construction business and created a strategy that showcases our craftsmanship while attracting the right clients.&quot;
                  </p>
                  <div>
                    <p className="font-normal text-sm md:text-lg uppercase">HAMED ASTANEH</p>
                    <p
                      className={`text-xs font-light ${
                        isDarkMode ? 'text-white/60' : 'text-[#000000]/60'
                      }`}
                      style={{ fontFamily: "'Cormorant Garamond', serif" }}
                    >
                      CEO &amp; Founder of Astaneh Construction
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Divider */}
      <div className={`w-full h-px ${isDarkMode ? 'bg-white/10' : 'bg-[#000000]/10'}`}></div>

      {/* ────────────────────────── WHAT WE DO — Cinéstory Approach ────────────────────────── */}
      <section id="what-we-do" className="py-14 sm:py-20 md:py-32 lg:py-40 px-4 sm:px-6 md:px-8 lg:px-16">
        <div className="max-w-6xl mx-auto">
          <p
            className={`scroll-reveal text-[10px] md:text-xs tracking-[0.3em] uppercase mb-3 md:mb-4 text-center ${
              isDarkMode ? 'text-white/40' : 'text-[#000000]/40'
            }`}
          >
            THE CINÉSTORY DIFFERENCE
          </p>
          <h2
            className={`scroll-reveal scroll-reveal-delay-1 text-lg sm:text-xl md:text-3xl lg:text-4xl font-normal text-center mb-4 md:mb-8 tracking-wide uppercase ${
              isDarkMode ? 'text-white' : 'text-[#000000]'
            }`}
          >
            STORIES THAT DON&apos;T JUST SUPPORT THE BRAND &mdash;<br className="hidden md:block" /> THEY BECOME THE BRAND
          </h2>
          <p
            className={`scroll-reveal scroll-reveal-delay-2 text-center text-sm md:text-xl mb-8 md:mb-16 max-w-3xl mx-auto font-light ${
              isDarkMode ? 'text-white/80' : 'text-[#000000]/80'
            }`}
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            We combine authentic storytelling, premium creative direction, and data-backed strategy into one system designed to reveal the real you — and make it unforgettable.
          </p>

          {/* Video Grid */}
          <div className="mb-8 md:mb-20">
            <h3
              className={`scroll-reveal text-base sm:text-lg md:text-2xl lg:text-3xl font-normal text-center mb-6 md:mb-12 tracking-wide uppercase ${
                isDarkMode ? 'text-white' : 'text-[#000000]'
              }`}
            >
              THIS COULD BE YOUR STORY
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4">
              {[
                'https://www.instagram.com/p/DQeuD-_jvcY/embed',
                'https://www.instagram.com/p/DOb2XGfDGYJ/embed',
                'https://www.instagram.com/p/DRkFtUbDWy6/embed',
                'https://www.instagram.com/p/DOMVVYwAUU0/embed',
              ].map((video, i) => (
                <div
                  key={i}
                  className={`scroll-reveal scroll-reveal-delay-${i + 1} aspect-[9/16] rounded-lg overflow-hidden shadow-lg`}
                >
                  <iframe
                    src={video}
                    className="w-full h-full"
                    frameBorder="0"
                    scrolling="no"
                    allowTransparency="true"
                    allow="encrypted-media"
                  ></iframe>
                </div>
              ))}
            </div>
          </div>

          {/* We help you */}
          <div>
            <h3
              className={`scroll-reveal text-lg sm:text-xl md:text-3xl lg:text-4xl font-normal text-center mb-8 md:mb-16 tracking-wide uppercase ${
                isDarkMode ? 'text-white' : 'text-[#000000]'
              }`}
            >
              WE HELP YOU
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-8">
              {[
                {
                  title: 'FIND YOUR NARRATIVE',
                  desc: "Uncover the authentic story at the heart of your brand — the one that resonates and connects.",
                },
                {
                  title: 'BUILD AUTHORITY',
                  desc: 'Position yourself as the go-to voice in your space through intentional, strategic storytelling.',
                },
                {
                  title: 'CREATE WITH PURPOSE',
                  desc: "Every piece of content serves a role — no filler, no fluff, just meaningful work that performs.",
                },
                {
                  title: 'ATTRACT THE RIGHT PEOPLE',
                  desc: 'Turn your presence into a magnet for clients who align with your values and vision.',
                },
                {
                  title: 'REFINE YOUR VISUAL IDENTITY',
                  desc: "Develop a distinctive, cinematic aesthetic that’s unmistakably yours.",
                },
                {
                  title: 'SPEAK CLEARER, NOT LOUDER',
                  desc: 'Cut through the noise with clarity, intention, and authenticity — not volume.',
                },
              ].map((item, i) => (
                <div
                  key={i}
                  className={`scroll-reveal scroll-reveal-delay-${(i % 3) + 1} ${
                    isDarkMode ? 'bg-[#0d0d0d]' : 'bg-[#F2F2F2]'
                  } p-5 md:p-8 rounded-lg backdrop-blur-sm border ${
                    isDarkMode ? 'border-white/5' : 'border-[#000000]/5'
                  }`}
                >
                  <h4
                    className={`text-sm md:text-lg font-normal mb-2 tracking-wide ${
                      isDarkMode ? 'text-white' : 'text-[#000000]'
                    }`}
                  >
                    {item.title}
                  </h4>
                  <p
                    className={`text-sm md:text-base font-light ${
                      isDarkMode ? 'text-white/80' : 'text-[#000000]/70'
                    }`}
                    style={{ fontFamily: "'Cormorant Garamond', serif" }}
                  >
                    {item.desc}
                  </p>
                </div>
              ))}
            </div>
            <p
              className={`scroll-reveal text-center text-sm md:text-xl mt-6 md:mt-12 max-w-3xl mx-auto font-light ${
                isDarkMode ? 'text-white/90' : 'text-[#000000]/80'
              }`}
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              Whether you’re a realtor, dentist, coach, creator, or business owner — Cinéstory adapts to YOUR story.
            </p>
          </div>
        </div>
      </section>

      {/* ────────────────────────── WHO THIS IS FOR ────────────────────────── */}
      <section
        id="who-this-is-for"
        className={`py-14 sm:py-20 md:py-32 lg:py-40 px-4 sm:px-6 md:px-8 lg:px-16 ${
          isDarkMode ? 'bg-[#0d0d0d]' : 'bg-[#F2F2F2]'
        }`}
      >
        <div className="max-w-6xl mx-auto">
          <h2 className="scroll-reveal text-lg sm:text-xl md:text-3xl lg:text-4xl font-normal text-center mb-8 md:mb-16 tracking-wide uppercase">
            PERFECT FOR BRANDS WHO WANT
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 max-w-4xl mx-auto">
            <div className="scroll-reveal-left scroll-reveal text-left">
              <h3 className="text-sm md:text-lg font-normal mb-2 md:mb-3 tracking-wide uppercase">
                AN AUTHENTIC NARRATIVE
              </h3>
              <p
                className={`text-sm md:text-base font-light ${
                  isDarkMode ? 'text-white/70' : 'text-[#000000]/70'
                }`}
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                Not random posting — a real, intentional story that reflects who you are.
              </p>
            </div>
            <div className="scroll-reveal-right scroll-reveal text-left">
              <h3 className="text-sm md:text-lg font-normal mb-2 md:mb-3 tracking-wide uppercase">
                A STRONG PERSONAL BRAND
              </h3>
              <p
                className={`text-sm md:text-base font-light ${
                  isDarkMode ? 'text-white/70' : 'text-[#000000]/70'
                }`}
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                Something distinctive, memorable, and aligned with your values.
              </p>
            </div>
            <div className="scroll-reveal-left scroll-reveal text-left">
              <h3 className="text-sm md:text-lg font-normal mb-2 md:mb-3 tracking-wide uppercase">
                STORIES THAT BUILD TRUST
              </h3>
              <p
                className={`text-sm md:text-base font-light ${
                  isDarkMode ? 'text-white/70' : 'text-[#000000]/70'
                }`}
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                Content rooted in perspective and authenticity, not trends and algorithms.
              </p>
            </div>
            <div className="scroll-reveal-right scroll-reveal text-left">
              <h3 className="text-sm md:text-lg font-normal mb-2 md:mb-3 tracking-wide uppercase">
                MORE CLIENTS &amp; BETTER POSITIONING
              </h3>
              <p
                className={`text-sm md:text-base font-light ${
                  isDarkMode ? 'text-white/70' : 'text-[#000000]/70'
                }`}
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                A brand that attracts high-quality opportunities naturally.
              </p>
            </div>
            <div className="scroll-reveal text-left md:col-span-2">
              <h3 className="text-sm md:text-lg font-normal mb-2 md:mb-3 tracking-wide uppercase">
                CONSISTENCY WITHOUT OVERTHINKING
              </h3>
              <p
                className={`text-sm md:text-base font-light ${
                  isDarkMode ? 'text-white/70' : 'text-[#000000]/70'
                }`}
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                Know what to share, when to share, and how to present your story.
              </p>
            </div>
          </div>
          <p
            className={`scroll-reveal text-sm md:text-xl max-w-3xl mx-auto mt-8 md:mt-12 text-center font-light ${
              isDarkMode ? 'text-white/70' : 'text-[#000000]/70'
            }`}
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            If you’re serious about building something real — this is for you.
          </p>
        </div>
      </section>

      {/* ────────────────────────── THE PROCESS ────────────────────────── */}
      <section id="process" className="py-14 sm:py-20 md:py-32 lg:py-40 px-4 sm:px-6 md:px-8 lg:px-16">
        <div className="max-w-6xl mx-auto">
          <p
            className={`scroll-reveal text-[10px] md:text-xs tracking-[0.3em] uppercase mb-3 md:mb-4 text-center ${
              isDarkMode ? 'text-white/40' : 'text-[#000000]/40'
            }`}
          >
            THE CINÉSTORY METHOD
          </p>
          <h2
            className={`scroll-reveal scroll-reveal-delay-1 text-lg sm:text-xl md:text-3xl lg:text-4xl font-normal text-center mb-4 md:mb-8 tracking-wide uppercase ${
              isDarkMode ? 'text-white' : 'text-[#000000]'
            }`}
          >
            THE 7-STEP BRAND &amp; STORYTELLING SYSTEM
          </h2>
          <p
            className={`scroll-reveal scroll-reveal-delay-2 text-center text-sm md:text-xl mb-8 md:mb-16 max-w-3xl mx-auto font-light ${
              isDarkMode ? 'text-white/80' : 'text-[#000000]/80'
            }`}
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            From brand confusion to narrative clarity in 7 intentional steps.
          </p>

          {/* 7 Steps */}
          <div className="space-y-8 md:space-y-12">
            {[
              {
                num: '1',
                title: 'DISCOVERY & ONBOARDING',
                content: (
                  <>
                    <p
                      className={`mb-2 text-sm md:text-lg font-light ${
                        isDarkMode ? 'text-white/80' : 'text-[#000000]/80'
                      }`}
                      style={{ fontFamily: "'Cormorant Garamond', serif" }}
                    >
                      We start by understanding your story — your business, your strengths, your goals, and the people you want to reach.
                    </p>
                    <p
                      className={`text-sm md:text-lg font-light ${
                        isDarkMode ? 'text-white/80' : 'text-[#000000]/80'
                      }`}
                      style={{ fontFamily: "'Cormorant Garamond', serif" }}
                    >
                      You complete our discovery form to give us everything we need to begin.
                    </p>
                  </>
                ),
              },
              {
                num: '2',
                title: 'BRAND POSITIONING FRAMEWORK',
                content: (
                  <>
                    <p
                      className={`mb-2 text-sm md:text-lg font-light ${
                        isDarkMode ? 'text-white/80' : 'text-[#000000]/80'
                      }`}
                      style={{ fontFamily: "'Cormorant Garamond', serif" }}
                    >
                      We define the 4 pillars of your personal brand:
                    </p>
                    <ul
                      className={`list-disc list-inside space-y-1 ml-4 text-sm md:text-lg font-light ${
                        isDarkMode ? 'text-white/80' : 'text-[#000000]/80'
                      }`}
                      style={{ fontFamily: "'Cormorant Garamond', serif" }}
                    >
                      <li>Beliefs</li>
                      <li>Style</li>
                      <li>Mission</li>
                      <li>Mechanism</li>
                    </ul>
                    <p
                      className={`mt-2 text-sm md:text-lg font-light ${
                        isDarkMode ? 'text-white/80' : 'text-[#000000]/80'
                      }`}
                      style={{ fontFamily: "'Cormorant Garamond', serif" }}
                    >
                      This becomes the foundation of your narrative and messaging.
                    </p>
                  </>
                ),
              },
              {
                num: '3',
                title: 'SOCIAL MEDIA ANALYSIS',
                content: (
                  <>
                    <p
                      className={`mb-2 text-sm md:text-lg font-light ${
                        isDarkMode ? 'text-white/80' : 'text-[#000000]/80'
                      }`}
                      style={{ fontFamily: "'Cormorant Garamond', serif" }}
                    >
                      We analyze your Instagram, YouTube, and TikTok to identify:
                    </p>
                    <ul
                      className={`list-disc list-inside space-y-1 ml-4 text-sm md:text-lg font-light ${
                        isDarkMode ? 'text-white/80' : 'text-[#000000]/80'
                      }`}
                      style={{ fontFamily: "'Cormorant Garamond', serif" }}
                    >
                      <li>Engagement patterns</li>
                      <li>Best performing formats</li>
                      <li>Weak spots &amp; missed opportunities</li>
                      <li>Visual identity quality</li>
                      <li>Hook effectiveness &amp; posting consistency</li>
                    </ul>
                    <p
                      className={`mt-2 text-sm md:text-lg font-light ${
                        isDarkMode ? 'text-white/80' : 'text-[#000000]/80'
                      }`}
                      style={{ fontFamily: "'Cormorant Garamond', serif" }}
                    >
                      This is your brand’s &quot;health check.&quot;
                    </p>
                  </>
                ),
              },
              {
                num: '4',
                title: 'PERFORMANCE SUMMARY',
                content: (
                  <>
                    <p
                      className={`mb-2 text-sm md:text-lg font-light ${
                        isDarkMode ? 'text-white/80' : 'text-[#000000]/80'
                      }`}
                      style={{ fontFamily: "'Cormorant Garamond', serif" }}
                    >
                      We break everything down clearly: what’s working, what’s not, what needs to change, and what opportunities you’re missing.
                    </p>
                    <p
                      className={`text-sm md:text-lg font-light ${
                        isDarkMode ? 'text-white/80' : 'text-[#000000]/80'
                      }`}
                      style={{ fontFamily: "'Cormorant Garamond', serif" }}
                    >
                      You’ll finally understand exactly where your brand stands.
                    </p>
                  </>
                ),
              },
              {
                num: '5',
                title: 'CUSTOM CONTENT STRATEGY',
                content: (
                  <>
                    <p
                      className={`mb-2 text-sm md:text-lg font-light ${
                        isDarkMode ? 'text-white/80' : 'text-[#000000]/80'
                      }`}
                      style={{ fontFamily: "'Cormorant Garamond', serif" }}
                    >
                      A complete storytelling strategy built just for you:
                    </p>
                    <ul
                      className={`list-disc list-inside space-y-1 ml-4 text-sm md:text-lg font-light ${
                        isDarkMode ? 'text-white/80' : 'text-[#000000]/80'
                      }`}
                      style={{ fontFamily: "'Cormorant Garamond', serif" }}
                    >
                      <li>15‑20 personalized content ideas</li>
                      <li>Hooks that increase watch time</li>
                      <li>Storytelling concepts &amp; narrative arcs</li>
                      <li>Authority‑building topics</li>
                      <li>CTA strategies &amp; brand‑building themes</li>
                    </ul>
                    <p
                      className={`mt-2 text-sm md:text-lg font-light ${
                        isDarkMode ? 'text-white/80' : 'text-[#000000]/80'
                      }`}
                      style={{ fontFamily: "'Cormorant Garamond', serif" }}
                    >
                      Everything is based on your niche + the data.
                    </p>
                  </>
                ),
              },
              {
                num: '6',
                title: 'WEEKLY POSTING FRAMEWORK',
                content: (
                  <>
                    <p
                      className={`mb-2 text-sm md:text-lg font-light ${
                        isDarkMode ? 'text-white/80' : 'text-[#000000]/80'
                      }`}
                      style={{ fontFamily: "'Cormorant Garamond', serif" }}
                    >
                      A simple weekly structure: what to post, when to post, why specific content works, and how to maintain consistency.
                    </p>
                    <p
                      className={`text-sm md:text-lg font-light ${
                        isDarkMode ? 'text-white/80' : 'text-[#000000]/80'
                      }`}
                      style={{ fontFamily: "'Cormorant Garamond', serif" }}
                    >
                      No more guessing. Just intention.
                    </p>
                  </>
                ),
              },
              {
                num: '7',
                title: 'REAL‑TIME DASHBOARD (OPTIONAL)',
                content: (
                  <>
                    <p
                      className={`mb-2 text-sm md:text-lg font-light ${
                        isDarkMode ? 'text-white/80' : 'text-[#000000]/80'
                      }`}
                      style={{ fontFamily: "'Cormorant Garamond', serif" }}
                    >
                      Monthly dashboard with engagement changes, best/worst posts, consistency score, data insights, and new opportunities.
                    </p>
                    <p
                      className={`text-sm md:text-lg font-light ${
                        isDarkMode ? 'text-white/80' : 'text-[#000000]/80'
                      }`}
                      style={{ fontFamily: "'Cormorant Garamond', serif" }}
                    >
                      You’ll always know exactly how your story is performing.
                    </p>
                  </>
                ),
              },
            ].map((step, index) => (
              <div
                key={step.num}
                className={`scroll-reveal ${
                  index % 2 === 0 ? 'scroll-reveal-left' : 'scroll-reveal-right'
                } ${isDarkMode ? 'bg-[#0d0d0d]' : 'bg-[#F2F2F2]'} p-5 md:p-8 rounded-lg backdrop-blur-sm border ${
                  isDarkMode ? 'border-white/5' : 'border-[#000000]/5'
                }`}
              >
                <div className="flex items-start gap-4 md:gap-6">
                  <div
                    className={`text-4xl md:text-6xl font-light ${
                      isDarkMode ? 'text-white/30' : 'text-[#000000]/30'
                    }`}
                    style={{ fontFamily: "'Cormorant Garamond', serif" }}
                  >
                    {step.num}
                  </div>
                  <div className="flex-1">
                    <h3
                      className={`text-sm md:text-xl font-normal mb-2 md:mb-4 tracking-wide ${
                        isDarkMode ? 'text-white' : 'text-[#000000]'
                      }`}
                    >
                      {step.title}
                    </h3>
                    {step.content}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ────────────────────────── WHAT YOU GET ────────────────────────── */}
      <section
        id="what-you-get"
        className={`py-14 sm:py-20 md:py-32 lg:py-40 px-4 sm:px-6 md:px-8 lg:px-16 ${
          isDarkMode ? 'bg-[#0d0d0d]' : 'bg-[#F2F2F2]'
        }`}
      >
        <div className="max-w-6xl mx-auto">
          <h2 className="scroll-reveal text-lg sm:text-xl md:text-3xl lg:text-4xl font-normal text-center mb-4 md:mb-8 tracking-wide uppercase">
            YOUR COMPLETE STORYTELLING SYSTEM INCLUDES
          </h2>
          <p
            className={`scroll-reveal scroll-reveal-delay-1 text-center text-sm md:text-xl mb-8 md:mb-16 max-w-3xl mx-auto font-light ${
              isDarkMode ? 'text-white/80' : 'text-[#000000]/80'
            }`}
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            Nothing vague. Nothing generic. Everything is built specifically for your story.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {[
              {
                title: 'FULL BRAND POSITIONING',
                desc: 'Clarifying your identity, values, and narrative voice.',
              },
              {
                title: 'SOCIAL MEDIA AUDIT (IG, TIKTOK, YOUTUBE)',
                desc: 'A complete performance breakdown.',
              },
              {
                title: 'STORYTELLING STRATEGY PLAYBOOK',
                desc: 'Your personalized, done-for-you narrative blueprint.',
              },
              {
                title: '20+ CONTENT IDEAS',
                desc: 'Based on trends, analytics, and your unique story.',
              },
              {
                title: 'HOOK + CAPTION LIBRARY',
                desc: 'High-converting, ready-to-use templates.',
              },
              {
                title: 'WEEKLY POSTING SYSTEM',
                desc: 'A structure that keeps you consistent and intentional.',
              },
              {
                title: 'MONTHLY DASHBOARD (OPTIONAL)',
                desc: 'Track growth and constantly refine your narrative.',
              },
            ].map((item, i) => (
              <div
                key={i}
                className={`scroll-reveal scroll-reveal-delay-${(i % 3) + 1} ${
                  isDarkMode ? 'bg-[#1a1a1a]' : 'bg-white'
                } p-5 md:p-8 rounded-lg shadow-lg border ${
                  isDarkMode ? 'border-white/5' : 'border-[#000000]/5'
                }`}
              >
                <h3 className="text-sm md:text-lg font-normal mb-2 md:mb-3 tracking-wide">
                  {item.title}
                </h3>
                <p
                  className={`text-sm md:text-base font-light ${
                    isDarkMode ? 'text-white/70' : 'text-[#000000]/70'
                  }`}
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ────────────────────────── PRICING ────────────────────────── */}
      <section id="pricing" className="py-14 sm:py-20 md:py-32 lg:py-40 px-4 sm:px-6 md:px-8 lg:px-16">
        <div className="max-w-6xl mx-auto">
          <p
            className={`scroll-reveal text-[10px] md:text-xs tracking-[0.3em] uppercase mb-3 md:mb-4 text-center ${
              isDarkMode ? 'text-white/40' : 'text-[#000000]/40'
            }`}
          >
            CIN&Eacute;STORY PACKAGES
          </p>
          <h2
            className={`scroll-reveal scroll-reveal-delay-1 text-lg sm:text-xl md:text-3xl lg:text-4xl font-normal text-center mb-4 md:mb-6 tracking-wide uppercase ${
              isDarkMode ? 'text-white' : 'text-[#000000]'
            }`}
          >
            CONTENT PACKAGES
          </h2>
          <p
            className={`scroll-reveal scroll-reveal-delay-2 text-center text-sm md:text-base mb-8 md:mb-16 max-w-2xl mx-auto font-light ${
              isDarkMode ? 'text-white/60' : 'text-[#000000]/60'
            }`}
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            3–6 month commitment. Consistency is what builds authority — and that&apos;s where the results come from.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mb-8 md:mb-16">

            {/* 4 Videos */}
            <div
              className={`scroll-reveal ${
                isDarkMode ? 'bg-[#0d0d0d]' : 'bg-[#F2F2F2]'
              } p-5 md:p-10 rounded-lg shadow-lg border ${
                isDarkMode ? 'border-white/5' : 'border-[#000000]/5'
              }`}
            >
              <h3 className="text-sm md:text-xl font-normal mb-1 tracking-wide uppercase">
                STARTER
              </h3>
              <p
                className={`text-xs md:text-sm mb-4 md:mb-8 font-light ${
                  isDarkMode ? 'text-white/50' : 'text-[#000000]/50'
                }`}
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                4 Videos / Month
              </p>
              <div className="mb-6 md:mb-8">
                <span className="text-3xl md:text-5xl font-normal">$1,399</span>
                <span
                  className={`text-sm md:text-base font-light ml-1 ${
                    isDarkMode ? 'text-white/50' : 'text-[#000000]/50'
                  }`}
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  /month
                </span>
              </div>
              <div className="space-y-3 mb-6 md:mb-10">
                {[
                  'Short-form vertical videos for Reels, TikTok & YouTube Shorts',
                  'Done-for-you scripting & creative direction',
                  'Monthly strategy call',
                  'Up to 2 filming locations per month',
                  'Personal brand & storytelling content mix',
                ].map((feature, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <i
                      className={`ri-check-line text-base md:text-lg ${
                        isDarkMode ? 'text-white' : 'text-[#000000]'
                      } flex-shrink-0 mt-0.5`}
                    ></i>
                    <span
                      className={`text-xs md:text-sm font-light ${
                        isDarkMode ? 'text-white/80' : 'text-[#000000]/80'
                      }`}
                      style={{ fontFamily: "'Cormorant Garamond', serif" }}
                    >
                      {feature}
                    </span>
                  </div>
                ))}
              </div>
              <a
                href="/home#contact"
                className={`inline-block w-full text-center py-2.5 md:py-4 ${
                  isDarkMode ? 'bg-white text-[#000000] hover:bg-white/90' : 'bg-[#000000] text-white hover:bg-[#000000]/90'
                } rounded-lg font-normal text-xs md:text-sm tracking-widest uppercase transition-colors cursor-pointer whitespace-nowrap`}
              >
                GET STARTED
              </a>
            </div>

            {/* 8 Videos — Most Popular */}
            <div
              className={`scroll-reveal scroll-reveal-delay-1 ${
                isDarkMode ? 'bg-[#1a1a1a]' : 'bg-[#000000]'
              } text-white p-5 md:p-10 rounded-lg shadow-2xl relative transform md:scale-105`}
            >
              <div className="absolute -top-3 md:-top-4 left-1/2 -translate-x-1/2 bg-white text-[#000000] px-3 md:px-6 py-1 text-[10px] md:text-xs font-normal uppercase rounded-full whitespace-nowrap shadow-lg tracking-widest">
                MOST POPULAR
              </div>
              <h3 className="text-sm md:text-xl font-normal mb-1 tracking-wide uppercase">
                GROWTH
              </h3>
              <p
                className="text-xs md:text-sm mb-4 md:mb-8 font-light text-white/50"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                8 Videos / Month
              </p>
              <div className="mb-6 md:mb-8">
                <span className="text-3xl md:text-5xl font-normal">$2,399</span>
                <span
                  className="text-sm md:text-base font-light ml-1 text-white/50"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  /month
                </span>
              </div>
              <div className="space-y-3 mb-6 md:mb-10">
                {[
                  'Everything in Starter',
                  'Double the content output for faster momentum',
                  'Broader content mix — authority, storytelling & community',
                  'Optimized posting cadence & campaign focus',
                  'Up to 2 filming locations per month',
                ].map((feature, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <i className="ri-check-line text-base md:text-lg text-white flex-shrink-0 mt-0.5"></i>
                    <span
                      className="text-xs md:text-sm font-light text-white/80"
                      style={{ fontFamily: "'Cormorant Garamond', serif" }}
                    >
                      {feature}
                    </span>
                  </div>
                ))}
              </div>
              <a
                href="/home#contact"
                className="block w-full text-center py-2.5 md:py-4 bg-white text-[#000000] hover:bg-white/90 rounded-lg font-normal text-xs md:text-sm tracking-widest uppercase transition-colors cursor-pointer whitespace-nowrap shadow-lg"
              >
                GET STARTED
              </a>
            </div>

            {/* 12 Videos */}
            <div
              className={`scroll-reveal scroll-reveal-delay-2 ${
                isDarkMode ? 'bg-[#0d0d0d]' : 'bg-[#F2F2F2]'
              } p-5 md:p-10 rounded-lg shadow-lg border ${
                isDarkMode ? 'border-white/5' : 'border-[#000000]/5'
              }`}
            >
              <h3 className="text-sm md:text-xl font-normal mb-1 tracking-wide uppercase">
                AUTHORITY
              </h3>
              <p
                className={`text-xs md:text-sm mb-4 md:mb-8 font-light ${
                  isDarkMode ? 'text-white/50' : 'text-[#000000]/50'
                }`}
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                12 Videos / Month
              </p>
              <div className="mb-6 md:mb-8">
                <span className="text-3xl md:text-5xl font-normal">$2,999</span>
                <span
                  className={`text-sm md:text-base font-light ml-1 ${
                    isDarkMode ? 'text-white/50' : 'text-[#000000]/50'
                  }`}
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  /month
                </span>
              </div>
              <div className="space-y-3 mb-6 md:mb-10">
                {[
                  'Everything in Growth',
                  'Maximum visibility & compounding content',
                  'Full strategic mix across all content pillars',
                  'Dedicated monthly strategy & direction call',
                  'Up to 2 filming locations per month',
                ].map((feature, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <i
                      className={`ri-check-line text-base md:text-lg ${
                        isDarkMode ? 'text-white' : 'text-[#000000]'
                      } flex-shrink-0 mt-0.5`}
                    ></i>
                    <span
                      className={`text-xs md:text-sm font-light ${
                        isDarkMode ? 'text-white/80' : 'text-[#000000]/80'
                      }`}
                      style={{ fontFamily: "'Cormorant Garamond', serif" }}
                    >
                      {feature}
                    </span>
                  </div>
                ))}
              </div>
              <a
                href="/home#contact"
                className={`inline-block w-full text-center py-2.5 md:py-4 ${
                  isDarkMode ? 'bg-white text-[#000000] hover:bg-white/90' : 'bg-[#000000] text-white hover:bg-[#000000]/90'
                } rounded-lg font-normal text-xs md:text-sm tracking-widest uppercase transition-colors cursor-pointer whitespace-nowrap shadow-lg`}
              >
                GET STARTED
              </a>
            </div>
          </div>

          {/* ── ADDITIONAL OFFERINGS ── */}
          <div className="scroll-reveal mb-8 md:mb-16">
            <div className="flex items-center gap-4 mb-8 md:mb-12">
              <div className={`flex-1 h-px ${isDarkMode ? 'bg-white/10' : 'bg-[#000000]/10'}`}></div>
              <p className={`text-[10px] md:text-xs tracking-[0.35em] uppercase font-normal ${isDarkMode ? 'text-white/40' : 'text-[#000000]/40'}`}>
                ADDITIONAL OFFERINGS
              </p>
              <div className={`flex-1 h-px ${isDarkMode ? 'bg-white/10' : 'bg-[#000000]/10'}`}></div>
            </div>

            <div className={`rounded-lg overflow-hidden border ${isDarkMode ? 'border-white/8 bg-[#0d0d0d]' : 'border-[#000000]/8 bg-[#F2F2F2]'}`}>
              <div className="grid grid-cols-1 lg:grid-cols-2">
                {/* Left — Info */}
                <div className="p-8 md:p-12 flex flex-col justify-center">
                  <p className={`text-[10px] md:text-xs tracking-[0.3em] uppercase mb-3 ${isDarkMode ? 'text-white/40' : 'text-[#000000]/40'}`}>
                    ADD-ON SERVICE
                  </p>
                  <h3 className="text-xl md:text-3xl font-normal tracking-wide uppercase mb-2">
                    Social Media Management
                  </h3>
                  <div className="flex items-baseline gap-1 mb-6 md:mb-8">
                    <span className="text-3xl md:text-5xl font-normal">$599</span>
                    <span className={`text-sm md:text-base font-light ${isDarkMode ? 'text-white/50' : 'text-[#000000]/50'}`} style={{ fontFamily: "'Cormorant Garamond', serif" }}>/month</span>
                  </div>
                  <p className={`text-sm md:text-base font-light leading-relaxed mb-8 ${isDarkMode ? 'text-white/70' : 'text-[#000000]/70'}`} style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                    For ongoing content optimization and engagement — a complete management layer that keeps your brand active, consistent, and growing between shoots.
                  </p>
                  <a
                    href="/home#contact"
                    className={`inline-block self-start px-8 py-3 md:py-4 ${
                      isDarkMode ? 'bg-white text-[#000000] hover:bg-white/90' : 'bg-[#000000] text-white hover:bg-[#000000]/90'
                    } text-[10px] md:text-xs tracking-widest uppercase transition-colors font-normal whitespace-nowrap cursor-pointer rounded-lg`}
                  >
                    ADD TO PACKAGE
                  </a>
                </div>

                {/* Right — Features */}
                <div className={`p-8 md:p-12 ${isDarkMode ? 'bg-white/[0.03] border-l border-white/5' : 'bg-white border-l border-[#000000]/5'}`}>
                  <p className={`text-[10px] md:text-xs tracking-[0.25em] uppercase mb-6 font-normal ${isDarkMode ? 'text-white/40' : 'text-[#000000]/40'}`}>
                    WHAT&apos;S INCLUDED
                  </p>
                  <div className="space-y-4">
                    {[
                      { icon: 'ri-image-line', text: 'Up to 12 Static Posts — custom descriptions & strategic hashtags' },
                      { icon: 'ri-focus-3-line', text: 'Consultation on Marketing Targets' },
                      { icon: 'ri-settings-3-line', text: 'Account Optimization' },
                      { icon: 'ri-calendar-schedule-line', text: 'Scheduling & Posting Content' },
                      { icon: 'ri-line-chart-line', text: 'Growing Your Online Presence' },
                      { icon: 'ri-group-line', text: 'Audience Engagement & Community Building' },
                      { icon: 'ri-bar-chart-2-line', text: 'Ongoing Support & Performance Tracking' },
                    ].map((item, i) => (
                      <div key={i} className="flex items-start gap-3 md:gap-4">
                        <div className={`w-8 h-8 flex items-center justify-center flex-shrink-0 rounded-md ${isDarkMode ? 'bg-white/5' : 'bg-[#000000]/5'}`}>
                          <i className={`${item.icon} text-sm ${isDarkMode ? 'text-white/60' : 'text-[#000000]/60'}`}></i>
                        </div>
                        <span className={`text-sm font-light leading-snug pt-1 ${isDarkMode ? 'text-white/80' : 'text-[#000000]/80'}`} style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                          {item.text}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Commitment note */}
          <div
            className={`scroll-reveal text-center mb-8 md:mb-16 px-4 py-5 md:py-6 rounded-lg border ${
              isDarkMode ? 'border-white/10 bg-white/[0.02]' : 'border-[#000000]/10 bg-[#000000]/[0.02]'
            }`}
          >
            <p
              className={`text-xs md:text-sm font-light tracking-wide ${
                isDarkMode ? 'text-white/60' : 'text-[#000000]/60'
              }`}
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              <span className={`font-normal uppercase tracking-widest text-xs ${isDarkMode ? 'text-white' : 'text-[#000000]'}`}>3–6 Month Commitment</span>
              &nbsp;&mdash;&nbsp;
              This is a momentum play. Consistency is what builds authority — and that&apos;s where the results come from.
            </p>
          </div>

          {/* CTA Block */}
          <div
            className={`scroll-reveal-scale scroll-reveal ${
              isDarkMode ? 'bg-[#0d0d0d]' : 'bg-[#000000]'
            } text-white p-6 md:p-12 rounded-lg text-center shadow-xl`}
          >
            <h2 className="text-base md:text-2xl lg:text-3xl font-normal mb-3 md:mb-6 tracking-wide uppercase">
              READY TO TELL A STORY THAT BECOMES YOUR BRAND?
            </h2>
            <p className="text-sm md:text-lg mb-2 md:mb-4 opacity-90 font-light" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Stop selling louder.
            </p>
            <p className="text-sm md:text-lg mb-2 md:mb-4 opacity-90 font-light" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Start speaking clearer.
            </p>
            <p className="text-sm md:text-lg mb-3 md:mb-6 opacity-90 font-light" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Build something authentic.
            </p>
            <p className="text-sm md:text-xl font-normal mb-4 md:mb-8 tracking-wide uppercase">THIS IS CIN&Eacute;STORY.</p>
            <a
              href="/home#contact"
              className="inline-block px-6 py-3 sm:px-12 md:py-4 bg-white text-[#000000] hover:bg-white/90 rounded-lg font-normal text-xs md:text-sm tracking-widest uppercase transition-colors cursor-pointer whitespace-nowrap shadow-lg"
            >
              START YOUR STORY
            </a>
            <p className="text-xs md:text-base mt-3 md:mt-6 opacity-80 font-light" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Intention. Perspective. Authenticity.
            </p>
          </div>
        </div>
      </section>

      {/* ────────────────────────── FAQs ────────────────────────── */}
      <section
        id="faqs"
        className={`py-14 sm:py-20 md:py-32 lg:py-40 px-4 sm:px-6 md:px-8 lg:px-16 ${
          isDarkMode ? 'bg-[#0d0d0d]' : 'bg-[#F2F2F2]'
        }`}
      >
        <div className="max-w-4xl mx-auto">
          <h2 className="scroll-reveal text-lg sm:text-xl md:text-3xl lg:text-4xl font-normal mb-12 md:mb-16 tracking-wide uppercase">
            FREQUENTLY ASKED QUESTIONS
          </h2>

          <div className="space-y-3 md:space-y-4">
            {faqs.map((faq, i) => (
              <div
                key={i}
                className={`scroll-reveal border-b ${
                  isDarkMode ? 'border-white/10' : 'border-[#000000]/10'
                }`}
              >
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full py-4 md:py-6 flex items-center justify-between text-left group cursor-pointer"
                >
                  <span className="text-sm md:text-lg font-normal pr-6 md:pr-8 tracking-wide uppercase">
                    {faq.question.toUpperCase()}
                  </span>
                  <div
                    className={`w-7 h-7 md:w-8 md:h-8 flex items-center justify-center flex-shrink-0 rounded-full ${
                      isDarkMode ? 'bg-white/10' : 'bg-[#000000]/10'
                    } transition-transform ${openFaq === i ? 'rotate-45' : ''}`}
                  >
                    <i className="ri-add-line text-lg md:text-xl"></i>
                  </div>
                </button>
                <div
                  className={`overflow-hidden transition-all duration-300 ${
                    openFaq === i ? 'max-h-96 pb-4 md:pb-6' : 'max-h-0'
                  }`}
                >
                  <p
                    className={`text-sm md:text-lg font-light leading-relaxed ${
                      isDarkMode ? 'text-white/70' : 'text-[#000000]/70'
                    }`}
                    style={{ fontFamily: "'Cormorant Garamond', serif" }}
                  >
                    {faq.answer}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ────────────────────────── FOOTER ────────────────────────── */}
      <footer
        className={`border-t ${isDarkMode ? 'border-white/10' : 'border-[#000000]/10'} py-8 md:py-12 px-6 md:px-8`}
      >
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6 md:gap-8 mb-6 md:mb-8">
            <Link to="/" className="block">
              <img
                src="https://static.readdy.ai/image/530c8f921e7d182176cffe22debeaa7c/f67bfe48594cc7cd8567f21111a8980c.png"
                alt="CIN\u00c9STORY"
                className={`h-5 md:h-7 w-auto ${isDarkMode ? 'brightness-0 invert' : ''}`}
              />
            </Link>

            <div className="flex flex-wrap gap-5 md:gap-8">
              <Link
                to="/home"
                className={`text-xs md:text-sm tracking-widest uppercase ${
                  isDarkMode
                    ? 'text-white/70 hover:text-white'
                    : 'text-[#000000]/70 hover:text-[#000000]'
                } transition-colors font-normal`}
              >
                HOME
              </Link>
              <a
                href="https://order.cinestate.ca/booking/new"
                target="_blank"
                rel="noopener noreferrer"
                className={`text-xs md:text-sm tracking-widest uppercase ${
                  isDarkMode
                    ? 'text-white/70 hover:text-white'
                    : 'text-[#000000]/70 hover:text-[#000000]'
                } transition-colors font-normal`}
              >
                LISTING PACKAGES
              </a>
              <Link
                to="/staging"
                className={`text-xs md:text-sm tracking-widest uppercase ${
                  isDarkMode
                    ? 'text-white/70 hover:text-white'
                    : 'text-[#000000]/70 hover:text-[#000000]'
                } transition-colors font-normal`}
              >
                HOME STAGING
              </Link>
              <Link
                to="/portfolio"
                className={`text-xs md:text-sm tracking-widest uppercase ${
                  isDarkMode
                    ? 'text-white/70 hover:text-white'
                    : 'text-[#000000]/70 hover:text-[#000000]'
                } transition-colors font-normal`}
              >
                PORTFOLIO
              </Link>
              <a
                href="/home#contact"
                className={`text-xs md:text-sm tracking-widest uppercase ${
                  isDarkMode
                    ? 'text-white/70 hover:text-white'
                    : 'text-[#000000]/70 hover:text-[#000000]'
                } transition-colors font-normal`}
              >
                CONTACT
              </a>
            </div>

            <div className="flex gap-3 md:gap-4">
              <a
                href="https://www.instagram.com/kianikanstudio?igsh=M3RoZ25wc25oZHY0"
                target="_blank"
                rel="noopener noreferrer"
                className={`w-7 h-7 md:w-8 md:h-8 flex items-center justify-center ${
                  isDarkMode ? 'text-white/70 hover:text-white' : 'text-[#000000]/70 hover:text-[#000000]'
                } transition-colors cursor-pointer`}
              >
                <i className="ri-instagram-line text-sm md:text-base"></i>
              </a>
              <a
                href="https://youtube.com/@kianikanstudio3017?si=QvudTCaWtHR5bmy8"
                target="_blank"
                rel="noopener noreferrer"
                className={`w-7 h-7 md:w-8 md:h-8 flex items-center justify-center ${
                  isDarkMode ? 'text-white/70 hover:text-white' : 'text-[#000000]/70 hover:text-[#000000]'
                } transition-colors cursor-pointer`}
              >
                <i className="ri-youtube-line text-sm md:text-base"></i>
              </a>
              <a
                href="https://www.tiktok.com/@kianikanstudio.ca?_r=1&_t=ZS-91yzSCjzfEY"
                target="_blank"
                rel="noopener noreferrer"
                className={`w-7 h-7 md:w-8 md:h-8 flex items-center justify-center ${
                  isDarkMode ? 'text-white/70 hover:text-white' : 'text-[#000000]/70 hover:text-[#000000]'
                } transition-colors cursor-pointer`}
              >
                <i className="ri-tiktok-line text-sm md:text-base"></i>
              </a>
              <a
                href="https://www.instagram.com/kianikanstudio?igsh=M3RoZ25wc25oZHY0"
                target="_blank"
                rel="noopener noreferrer"
                className={`w-7 h-7 md:w-8 md:h-8 flex items-center justify-center ${
                  isDarkMode ? 'text-white/70 hover:text-white' : 'text-[#000000]/70 hover:text-[#000000]'
                } transition-colors cursor-pointer`}
              >
                <i className="ri-facebook-fill text-sm md:text-base"></i>
              </a>
            </div>
          </div>

          <div className={`w-full h-px ${isDarkMode ? 'bg-white/10' : 'bg-[#000000]/10'} mb-6 md:mb-8`}></div>

          <div
            className={`flex flex-col md:flex-row justify-between items-center gap-3 md:gap-4 text-xs md:text-sm ${
              isDarkMode ? 'text-white/50' : 'text-[#000000]/50'
            }`}
          >
            <p className="font-light" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              © 2025 Cin\u00e9story — A Cin\u00e9state Brand. All rights reserved.
            </p>
            <a
              href="https://readdy.ai/?ref=logo"
              className={`${isDarkMode ? 'hover:text-white' : 'hover:text-[#000000]'} transition-colors font-light`}
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              Powered by Readdy
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
